<template>
  <div class="staff-requests-overview">
    <div class="staff-requests-overview__inner">
      <div class="staff-requests-overview__filters">
        <div v-if="isStaff || showStatusFilter" class="FilterAspect">
          <h5 class="FilterAspect__Title">Filter by Status</h5>
          <md-field>
            <label for="status">Status</label>
            <md-select
              name="status"
              id="status"
              @md-selected="refresh"
              v-model="requests.filters.statuses"
              multiple
            >
              <md-option
                v-for="status in Request.options.REQUEST_STATUS_CHOICES"
                :value="status.value"
                :key="status.value"
              >
                {{ status.label }}
              </md-option>
            </md-select>
            <div
              @click="clearArrayFilter('statuses')"
              class="icon-container"
              v-if="hasArrayFilter('statuses')"
            >
              <md-icon class="FilterAspect__Clear">close</md-icon>
            </div>
          </md-field>
        </div>
        <div v-if="isStaff || showEventsFilter" class="FilterAspect">
          <h5 class="FilterAspect__Title">Filter by Event</h5>
          <md-field>
            <label for="eventCategories">Event</label>
            <md-select
              name="eventCategories"
              id="eventCategories"
              @md-selected="refresh"
              v-model="requests.filters.reportableEventCategories"
              multiple
            >
              <md-option v-for="event in eventCategories.list" :value="event.id" :key="event.id">
                {{ event.name }}
              </md-option>
            </md-select>
            <div
              @click="clearArrayFilter('eventCategories')"
              class="icon-container"
              v-if="hasArrayFilter('eventCategories')"
            >
              <md-icon class="FilterAspect__Clear">close</md-icon>
            </div>
          </md-field>
        </div>
        <div v-if="isStaff" class="FilterAspect">
          <h5 class="FilterAspect__Title">Filter by Assigned</h5>
          <md-field>
            <label for="assigned">Staff Member</label>
            <md-select
              name="assigned"
              id="assigned"
              @md-selected="refresh"
              v-model="requests.filters.assignedTo"
            >
              <md-option v-for="staff in staff.list" :value="staff.id" :key="staff.id">
                {{ staff.fullName }}
              </md-option>
              <md-option value="None" key="none"> Unassigned </md-option>
            </md-select>
            <div @click="resetAssigned" class="icon-container" v-if="requests.filters.assignedTo">
              <md-icon class="FilterAspect__Clear">close</md-icon>
            </div>
          </md-field>
        </div>
        <div v-if="showReferredFilter" class="FilterAspect">
          <h5 class="FilterAspect__Title">Filter by Referred</h5>
          <md-field>
            <label for="referred">Referred</label>
            <md-select name="referred" @md-selected="selectReferred" v-model="selectedReferred">
              <md-option :value="REFERRED_ONLY" :key="REFERRED_ONLY">
                Referred Only
              </md-option>

              <md-option :value="NON_REFERRED_ONLY" :key="NON_REFERRED_ONLY">
                Non-Referred Only
              </md-option>
            </md-select>
            <div
              @click="clearReferredFilter"
              class="icon-container"
              v-if="selectedReferred !== null"
            >
              <md-icon class="FilterAspect__Clear">close</md-icon>
            </div>
          </md-field>
        </div>

        <div v-if="isStaff" class="FilterAspect">
          <h5 class="FilterAspect__Title">Filter by Organization</h5>
          <md-field>
            <label for="organizations">Organizations</label>
            <md-select
              name="organizations"
              @md-selected="refresh"
              v-model="requests.filters.organizations"
              multiple
            >
              <md-option v-for="org in organizations.list" :value="org.id" :key="org.id">
                {{ org.name }}
              </md-option>
            </md-select>
            <div
              @click="clearArrayFilter('organizations')"
              class="icon-container"
              v-if="hasArrayFilter('organizations')"
            >
              <md-icon class="FilterAspect__Clear">close</md-icon>
            </div>
          </md-field>
        </div>

        <div v-if="organizationDetails.subOrganizations.length" class="FilterAspect">
          <h5 class="FilterAspect__Title">Filter by Sub Organization</h5>
          <md-field>
            <label for="suborganizations">Sub Organizations</label>
            <md-select
              name="organizations"
              @md-selected="refresh"
              v-model="requests.filters.subOrganizations"
              multiple
            >
              <md-option v-for="subOrg in showSubOrgFilter" :value="subOrg.id" :key="subOrg.id">
                {{ subOrg.name }}
              </md-option>
            </md-select>
            <div
              @click="clearArrayFilter('subOrganizations')"
              class="icon-container"
              v-if="hasArrayFilter('subOrganizations')"
            >
              <md-icon class="FilterAspect__Clear">close</md-icon>
            </div>
          </md-field>
        </div>
      </div>
      <div class="staff-requests-overview__table">
        <TableHeader :title="'Requests'" :collection="requests" />
        <LoadSpinner v-if="requests.refreshing" style="padding: 2rem 0" />
        <div class="table-wrapper" v-if="!requests.refreshing">
          <md-table>
            <md-table-row class="header-row">
              <md-table-head>
                Name
                <span class="sort-icon" @click="onSortByField('employee__last_name')">
                  <md-icon v-if="sorts.employee__last_name === null">sort</md-icon>
                  <md-icon v-else-if="sorts.employee__last_name === ASC">arrow_drop_down</md-icon>
                  <md-icon v-else>arrow_drop_up</md-icon>
                </span>
              </md-table-head>
              <md-table-head v-if="!isReadOnlyAdmin()">
                Organization
                <span class="sort-icon" @click="onSortByField('employee__organization__name')">
                  <md-icon v-if="sorts.employee__organization__name === null">sort</md-icon>
                  <md-icon v-else-if="sorts.employee__organization__name === ASC">
                    arrow_drop_down
                  </md-icon>
                  <md-icon v-else>arrow_drop_up</md-icon>
                </span>
              </md-table-head>
              <md-table-head v-show="false" v-if="organizationDetails.subOrganizations.length">
                Sub Organization
                <span class="sort-icon" @click="onSortByField('employee__sub_organization__name')">
                  <md-icon v-if="sorts.employee__sub_organization__name === null">sort</md-icon>
                  <md-icon v-else-if="sorts.employee__sub_organization__name === ASC">
                    arrow_drop_down
                  </md-icon>
                  <md-icon v-else>arrow_drop_up</md-icon>
                </span>
              </md-table-head>
              <md-table-head>
                Date Submitted
                <span class="sort-icon" @click="onSortByField('submitted_on')">
                  <md-icon v-if="sorts.submitted_on === null">sort</md-icon>
                  <md-icon v-else-if="sorts.submitted_on === ASC">arrow_drop_down</md-icon>
                  <md-icon v-else>arrow_drop_up</md-icon>
                </span>
              </md-table-head>
              <md-table-head>
                Last Active
                <span class="sort-icon" @click="onSortByField('last_active')">
                  <md-icon v-if="sorts.last_active === null">sort</md-icon>
                  <md-icon v-else-if="sorts.last_active === ASC">arrow_drop_down</md-icon>
                  <md-icon v-else>arrow_drop_up</md-icon>
                </span>
              </md-table-head>
              <md-table-head>
                Last Communication
              </md-table-head>
              <md-table-head>
                Follow-up
                <span class="sort-icon" @click="onSortByField('follow_up_date')">
                  <md-icon v-if="sorts.follow_up_date === null">sort</md-icon>
                  <md-icon v-else-if="sorts.follow_up_date === ASC">arrow_drop_down</md-icon>
                  <md-icon v-else>arrow_drop_up</md-icon>
                </span>
              </md-table-head>
              <!-- Note(Bruno 4-2-20): commenting out as per CAN-338, Marcy said to keep as we may use this column later -->
              <!-- <md-table-head>Net Received</md-table-head> -->
              <md-table-head> Request Amount </md-table-head>
              <!-- <md-table-head v-if="showReferredColumn">
                Referred?
                <span class="sort-icon" @click="onSortByField('referred_on')">
                  <md-icon v-if="sorts.referred_on === null">sort</md-icon>
                  <md-icon v-else-if="sorts.referred_on === ASC">arrow_drop_down</md-icon>
                  <md-icon v-else>arrow_drop_up</md-icon>
                </span>
              </md-table-head> -->
              <md-table-head> Status </md-table-head>
              <md-table-head> Disbursed Amount </md-table-head>
            </md-table-row>

            <md-table-row
              class="body-row"
              :class="{ 'gray-row': request.employeeInactive }"
              v-for="request in requests.list"
              :key="request.id"
              @click.native="() => onRowClick(request)"
            >
              <md-table-cell>{{
                request.employeeRef.fullName || $gettext('Unnamed User')
              }}</md-table-cell>
              <md-table-cell v-if="!isReadOnlyAdmin()">{{
                (request.organizationRef && request.organizationRef.name) || 'Unnamed Org.'
              }}</md-table-cell>
              <md-table-cell v-show="false">{{
                (request.employeeRef &&
                  request.employeeRef.subOrganizationRef &&
                  request.employeeRef.subOrganizationRef.name) ||
                  'Unnamed Sub Org.'
              }}</md-table-cell>
              <md-table-cell>{{
                request.submittedOn | formatDateShort($language.current)
              }}</md-table-cell>
              <!-- Note(Bruno 4-2-20): commenting out as per CAN-338, Marcy said to keep as we may use this column later -->
              <!-- <md-table-cell>{{ request.netReceived | currency }}</md-table-cell> -->
              <md-table-cell>{{
                request.lastActive | formatDateShort($language.current)
              }}</md-table-cell>

              <md-table-cell>
                <div class="last-communicated">
                  <div v-if="request.lastUserMessagedAt" class="user">
                    Applicant: {{ request.lastUserMessagedAt | formatDateShort($language.current) }}
                    <div>
                      {{ dateToWords(request.lastUserMessagedAt) }}
                    </div>
                  </div>
                  <div v-if="request.lastAdminMessagedAt" class="admin">
                    Admin: {{ request.lastAdminMessagedAt | formatDateShort($language.current) }}
                    <div>
                      {{ dateToWords(request.lastAdminMessagedAt) }}
                    </div>
                  </div>
                </div>
              </md-table-cell>

              <md-table-cell>{{
                request.followUpDate | formatDateShort($language.current) || "N/A"
              }}</md-table-cell>

              <md-table-cell>{{ request.amount | currency }}</md-table-cell>
              <!-- <md-table-cell v-if="showReferredColumn">{{
                request.isReferred ? 'Yes' : 'No'
              }}</md-table-cell> -->
              <md-table-cell v-if="request.status !== 'SUBMITTED'">{{
                request.status
              }}</md-table-cell>
              <md-table-cell v-else>{{ request.submittedDisplay }}</md-table-cell>
              <md-table-cell>{{ request.approvedFunds | currency }}</md-table-cell>
            </md-table-row>
          </md-table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { LoadSpinner, TableHeader } from '@/components'
import { mapState } from 'vuex'
import Request from '@/services/requests'
import EventCategory from '@/services/eventCategories'
import Organization from '@/services/organizations'
import CollectionManager from '@/services/collectionManager'
import AdminPermissions from '@/services/adminPermissions'
import Staff from '@/services/staff'
import Pagination from '@/services/pagination'
import { DateTime } from 'luxon'


const REFERRED_ONLY = 'REFERRED_ONLY'
const NON_REFERRED_ONLY = 'NON_REFERRED_ONLY'
const ASC = 'ASC'
const DESC = 'DESC'

export default {
  name: 'RequestOverview',
  components: {
    LoadSpinner,
    TableHeader,
  },
  data: function() {
    return {
      Request,
      REFERRED_ONLY,
      NON_REFERRED_ONLY,
      selectedReferred: null,
      requests: CollectionManager.create({
        ModelClass: Request,
        filters: {
          ordering: '-datetime_created',
          reportableEventCategories: [],
          statuses: [],
          organizations: [],
          referredOnly: null,
          nonReferredOnly: false,
          submittedStatuses: [],
          assignedTo: '',
          subOrganizations: [],
        },
      }),
      sorts: {
        employee__last_name: null,
        employee__organization__name: null,
        employee__sub_organization__name: null,
        submitted_on: null,
        referred_on: null,
        _status: null,
        last_active: null,
        follow_up_date: null,
      },
      ASC,
      DESC,
      eventCategories: CollectionManager.create({
        ModelClass: EventCategory,
        pagination: Pagination.create({ pageSize: 50 }),
      }),
      organizations: CollectionManager.create({
        ModelClass: Organization,
        pagination: Pagination.create({ pageSize: 50 }),
      }),
      staff: CollectionManager.create({
        ModelClass: Staff,
      }),
    }
  },
  async created() {
    this.refresh()
    this.organizations.refresh()
    this.eventCategories.refresh()
    this.staff.refresh()
  },
  methods: {
    isReadOnlyAdmin() {
      if (
        this.user.isAdmin &&
        (
          this.user.adminPermissions.name === AdminPermissions.permissionNames.READ_ONLY
          || this.user.adminPermissions.name === AdminPermissions.permissionNames.TCR_ADMIN
        )
      ) {
        return true
      }

      return false
    },
    refresh() {
      this.requests.refresh()
    },
    dateToWords(date) {
      return DateTime.fromJSDate(date).toRelativeCalendar()
    },
    onRowClick(request) {
      const routeData = this.$router.resolve({
        name: 'StaffRequestDetail',
        params: {
          id: request.id,
        },
      })

      window.open(routeData.href, '_blank')
    },
    onSortByField(field) {
      // toggle ascending / descending, while resetting other sorts (currently only filtering by one at a time)
      let ascending = this.sorts[field] === DESC || this.sorts[field] == null
      let newSortState = {
        employee__last_name: null,
        employee__organization__name: null,
        employee__sub_organization__name: null,
        submitted_on: null,
        amount: null,
        referred_on: null,
        _status: null,
        last_active: null,
        follow_up_date: null,
      }
      newSortState[field] = ascending ? ASC : DESC
      this.sorts = newSortState

      let term = `${ascending ? '' : '-'}${field}`
      // if sorting by name, include first_name as second sorting criteria
      if (field == 'employee__last_name') {
        term += `,${ascending ? '' : '-'}${'employee__first_name'}`
      }
      this.updateRequestSort({ ordering: term })
    },
    updateRequestSort(filtersObj = {}) {
      this.requests = CollectionManager.create({
        ModelClass: Request,
        filters: {
          ...this.requests.filters,
          ...filtersObj,
        },
      })
      this.refresh()
    },
    selectReferred(newValue) {
      if (newValue == REFERRED_ONLY) {
        this.requests.filters.referredOnly = true
        this.requests.filters.nonReferredOnly = false
      } else {
        this.requests.filters.referredOnly = false
        this.requests.filters.nonReferredOnly = true
      }

      this.refresh()
    },
    hasArrayFilter(filterKey) {
      let container = this.requests.filters[filterKey]
      return container ? container.length > 0 : false
    },
    clearArrayFilter(filterKey) {
      this.requests.filters[filterKey] = []
    },
    clearReferredFilter() {
      this.selectedReferred = null
      this.requests.filters.referredOnly = null
      this.requests.filters.nonReferredOnly = null

      this.refresh()
    },
    resetAssigned() {
      this.requests.filters.assignedTo = ''
      this.refresh()
    },
  },
  computed: {
    ...mapState(['user', 'organizationDetails']),
    showSubOrgFilter() {
      return this.organizationDetails.subOrganizations
    },
    showReferredColumn() {
      if (
        this.user.isAdmin &&
        this.user.adminPermissions.name === AdminPermissions.permissionNames.READ_ONLY
      ) {
        return false
      }

      return true
    },
    isStaff() {
      return this.user.isStaff
    },
    /* Don't show "referred" filter if user is admin who can only see referred requests anyway */
    showReferredFilter() {
      if (
        (this.user.isAdmin &&
          this.user.adminPermissions.viewWhichRequests ===
            AdminPermissions.options.REFERRED_REQUESTS_ONLY) ||
        (this.user.isAdmin &&
          (
            this.user.adminPermissions.name === AdminPermissions.permissionNames.READ_ONLY
            || this.user.adminPermissions.name === AdminPermissions.permissionNames.TCR_ADMIN
          )
        )
      ) {
        return false
      }
      return true
    },
    showStatusFilter() {
      if(this.user.isAdmin && this.user.adminPermissions.name === AdminPermissions.permissionNames.TCR_ADMIN)
        return true

      return false
    },
    showEventsFilter() {
      if(this.user.isAdmin && this.user.adminPermissions.name === AdminPermissions.permissionNames.TCR_ADMIN)
        return true

      return false
    }
  },
}
</script>

<style lang="scss" scoped>
@import '../../styles/variables';
@import '../../styles/mixins/containers';

.staff-requests-overview {
  @include screen-container($mobile-padding: 1rem 0.5rem, $desktop-max-width: 112rem);

  &__inner {
    max-width: 100%;
  }

  @media (min-width: $mobile-break-point) {
    &__inner {
      display: flex;
    }

    &__filters {
      flex: 1;
      padding: 1rem 2rem 0 1rem;
      min-width: 16rem;
    }

    &__table {
      flex: 8;
      overflow-x: auto;
    }
  }
}

.last-communicated {
  display: flex;
  flex-direction: column;
  width: 200px;

  .user {
    display: flex;
    flex-direction: column;
  }

  .admin {
    display: flex;
    flex-direction: column;
  }
}

// Table Styles
.header-row {
  background-color: $soft-gray;

  & > .md-table-head {
    font-size: 0.9rem;
  }
}

.body-row {
  & > .md-table-cell {
    border-top: 1px solid $soft-gray;
    padding: 0.5rem;
  }

  &:hover {
    background-color: $off-white;
    cursor: pointer;
  }
}

.warning-icon {
  color: $ruby;
}

.gray-row {
  background-color: $mid-gray;

  &:hover {
    background-color: $gray;
  }
}

.updated-icons {
  display: flex;
  flex-direction: row;
  width: 100%;
  justify-content: space-between;
  align-items: center;
}

.icon-container {
  display: flex;
  justify-content: center;
  align-items: center;

  &:hover {
    background-color: $off-white;
    cursor: pointer;
  }
}

// Filter UI

.FilterAspect {
  margin-bottom: 32px;
}

.FilterAspect__Title {
  margin-bottom: 0;
}

.FilterAspect__Clear {
  color: gray;
}
</style>
